import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'

export type PageTabOption<T extends string | number = string | number> = {
  value: T
  label: string
  badge?: number
  disabled?: boolean
}

export interface PageTabsConfig {
  tabs: PageTabOption[]
  value: string | number
  onChange: (value: string | number) => void
  ariaLabel?: string
  label?: string
}

interface PageTabsContextValue {
  config: PageTabsConfig | null
  setConfig: (config: PageTabsConfig | null) => void
}

const PageTabsContext = createContext<PageTabsContextValue | null>(null)

export const PageTabsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<PageTabsConfig | null>(null)
  const value = useMemo<PageTabsContextValue>(() => ({ config, setConfig }), [config])
  return <PageTabsContext.Provider value={value}>{children}</PageTabsContext.Provider>
}

/** Read-only access for the bottom bar. Returns null when no provider is mounted. */
export const usePageTabsContext = (): PageTabsContextValue | null => useContext(PageTabsContext)

interface RegisterConfig {
  tabs: PageTabOption[]
  value: string | number
  onChange: (value: string | number) => void
  ariaLabel?: string
  label?: string
}

/**
 * Publishes a page's tabs to the shared bottom bar while `active` is true.
 * Returns true when a provider accepted the registration (so the caller can
 * skip rendering an inline fallback). Uses a value signature so inline
 * `tabs`/`onChange` identities don't retrigger the effect.
 */
export function useRegisterPageTabs(active: boolean, cfg: RegisterConfig): boolean {
  const ctx = useContext(PageTabsContext)
  const setConfig = ctx?.setConfig
  const latest = useRef(cfg)
  latest.current = cfg

  const signature = active
    ? JSON.stringify({
        v: cfg.value,
        a: cfg.ariaLabel ?? '',
        l: cfg.label ?? '',
        t: cfg.tabs.map(tab => [tab.value, tab.label, tab.badge ?? 0, tab.disabled ? 1 : 0]),
      })
    : ''

  useEffect(() => {
    if (!setConfig) return
    if (!active) {
      setConfig(null)
      return
    }
    setConfig({
      tabs: latest.current.tabs,
      value: latest.current.value,
      onChange: (value: string | number) => latest.current.onChange(value),
      ariaLabel: latest.current.ariaLabel,
      label: latest.current.label,
    })
  }, [active, signature, setConfig])

  useEffect(() => {
    return () => setConfig?.(null)
  }, [setConfig])

  return Boolean(setConfig)
}
